import { useEffect, useMemo, useContext, Children } from "react";
import { Link, generatePath, useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Card, Spin, TabsProps, Empty } from "antd";
import { useRequest } from "ahooks";
import { PPMService, getProject, getProjectPermissionsApi } from "@/services";
import { IthHelmet, PpmProjectListRefreshEventName } from "@/components";
import ProjectInformation from "./information";
import ProjectContracts from "./contracts";
import PlmCosts from "./plmCosts";
import ProjectPaymentNodes from "./paymentNodes";
import ProjectWpReports from "./wpReports";
import PPMTabs from "@/components/tabs"
import { PlmContext } from "@/contexts"
import { isIthClient } from "@/utils"
import { getProjectContracts } from "@/services"
import Dashboard from "./dashboard";
import { isDev } from '@/config'
import { ProjectDetailContext } from './constant'
import BudgetNew from "./budgetNew";
import CostsNew from "./costsNew";
import Milestone from "./milestone";
import MilestoneZb from "./milestoneZb";
import ControlNodes from "./controlNodes";
import ControlNodesZb from "./controlNodesZb";
import Payables from "./payables";
import DesignHQ from "./designHQ";
import DesignHQNew from "./designHQNew";
import { k_permission } from './project'

const ProjectDetail: React.FC = () => {
  const plmContextVal = useContext(PlmContext)
  const { projectID, tab } = useParams();

  const navigate = useNavigate();

  const { data: pmProject, loading: pmProjectLoading, run: runGetPmProject } = useRequest(getProject, {
    manual: true,
    debounceWait: 300,
  });

  const { data: projectContracts } = useRequest(getProjectContracts, {
    defaultParams: [Number(projectID)]
  })

  const { data: projectPermissions, loading: permissionsLoading } = useRequest(getProjectPermissionsApi, {
    defaultParams: [Number(projectID)]
  })

  const { data, loading, refresh } = useRequest(
    async () => {
      return projectID ? await PPMService.getProject(Number(projectID)) : undefined;
    },
    {
      refreshDeps: [projectID],
      onSuccess: (res) => {
        if (res && res.id) {
          plmContextVal.onProjectChange && plmContextVal.onProjectChange(res)
        }
        // if (!res?.profile?.code) return;
        // runGetPmProject(res.profile.code);
      }
    },
  );

  useEffect(() => {
    const handleSuccess = () => {
      refresh();
    };
    window.addEventListener(PpmProjectListRefreshEventName, handleSuccess);
    return () => {
      window.removeEventListener(PpmProjectListRefreshEventName, handleSuccess);
    };
  }, [refresh]);

  const tabItems = useMemo(() => {
    if (!projectPermissions) return []
    const permissions = projectPermissions.permissions || []
    let items = []
    if (permissions.includes(k_permission.dashboardView)) {
      items.push({
        key: 'dashboard',
        label: '项目驾驶舱',
        children: (<Dashboard project={data} />)
      })
    }
    if (isDev || permissions.includes(k_permission.designPackageView)) {
      items.push({
        key: 'designhq',
        label: '项目经理职责自查',
        children: (<DesignHQ />),
      })
      items.push({ key: 'designhq-new', label: '项目经理职责自查(新)', children: (<DesignHQNew />) })
    }
    if (permissions.includes(k_permission.milestoneView)) {
      items.push({
        key: 'milestone',
        label: '里程碑计划',
        children: (<Milestone />),
      })
      items.push({
        key: 'milestone-zb',
        label: '总包里程碑',
        children: (<MilestoneZb />),
      })
    }
    if (permissions.includes(k_permission.teamSpaceView)) {
      items.push({
        key: 'boards',
        label: '多项目看板',
        children: <></>,
        isLink: true,
        path: `/projects/${projectID}/board`,
        url: generatePath(`/projects/${projectID}/board`),
      })
    }
    if (isDev || permissions.includes(k_permission.projectControlView)) {
      items.push({
        key: 'controlNodes',
        label: '项目管控节点',
        children: (<ControlNodes />)
      })
      items.push({
        key: 'controlNodes-zb',
        label: '总包项目管控节点',
        children: (<ControlNodesZb key={projectID} />)
      })
    }
    if (items.length > 0 && data?.profile?.typeId === 1) {
      return items
    }
    
    // if (permissions.includes(k_permission.budgetsView)) {
    //   items.push({
    //     key: 'budgets',
    //     label: '标准任务比例',
    //     children: (<BudgetNew />),
    //   })
    // }
    if (isDev || permissions.includes(k_permission.mainContractorPayment)) {
      items.push({
        key: 'payables',
        label: '应付款管理',
        children: (<Payables />),
      })
    }
    if (permissions.includes(k_permission.costsView)) {
      items.push({
        key: 'costs',
        label: '成本管理',
        children: (<CostsNew />),
      })
    }
    
    if (permissions.includes(k_permission.paymentNodesView)) {
      items.push({
        key: 'paymentNodes',
        label: '收款节点',
        children: (<ProjectPaymentNodes projectID={Number(projectID)} contracts={projectContracts} />),
      });
    }
    // if (data?.profile?.code && permissions.includes(k_permission.plmcostsView)) {
    //   items.push({
    //     key: 'plmcosts',
    //     label: '总包合同一览',
    //     children: (<PlmCosts project={data} canEdit={permissions.includes(k_permission.plmcostsEdit)} />),
    //   });
    // }
    // if (permissions.includes(k_permission.wpReportsView)) {
    //   items.push({
    //     key: 'wpReports',
    //     label: '工作报表',
    //     children: (<ProjectWpReports project={data} />),
    //   })
    // }
    if (permissions.includes(k_permission.contractsView)) {
      items.push({
        key: 'contracts',
        label: '手册&模板',
        children: (<ProjectContracts />),
      })
    }
    return items;
  }, [data, pmProject, projectContracts, projectID, projectPermissions]);

  useEffect(() => {
    if (!data || !pmProject || tabItems.length <= 0) return;
    const tabNames = tabItems.map(item => item.key);
    if (!tab || !tabNames.includes(tab)) {
      navigate(generatePath('/projects/:projectID/:tab', { projectID: String(projectID), tab: tabNames[0] }));
    }
  }, [data, pmProject, projectID, navigate, tab, tabItems]);

  const onTabChange: TabsProps['onChange'] = (activeKey) => {
    const item = tabItems.find(v => v.key === activeKey)
    if (item && item.isLink) {
      navigate(item.path)
      // window.open(item.url)
      return
    }
    navigate(generatePath('/projects/:projectID/:tab', { projectID: String(projectID), tab: String(activeKey) }));
  };

  return (
    <ProjectDetailContext.Provider value={{ contacts: projectContracts, permissions: projectPermissions ? projectPermissions.permissions : [], project: data }}>
    <Spin spinning={loading || pmProjectLoading || permissionsLoading}>
        <div className="overflow-hidden">
        <IthHelmet pageTitle={data?.name} report={!!data} />
        {isIthClient() && (
          <Breadcrumb
            items={[
              {
                key: 'list',
                title: (<Link to="/projects">项目</Link>),
              },
              {
                key: 'detail',
                title: '项目详情',
              },
            ]}
          />
        )}
        {((!loading && (!data || !data.id)) || (!permissionsLoading && tabItems.length <= 0)) ? (
          <Empty description="无权限查看该项目" />
        ) : (
          <Card
            title={isIthClient() ? data?.name || ' ' : null}
            className={`${isIthClient() ? 'mt-4' : ''} min-w-[1000px]`}
            styles={{ body: { padding: 16 } }}
          >
            <PPMTabs
              activeKey={tab}
              items={tabItems}
              onChange={onTabChange}
            />
          </Card>
        )}
      </div>
    </Spin>
    </ProjectDetailContext.Provider>
  );
};

export default ProjectDetail;
